/** React imports */
import React, { useMemo } from 'react';
/** App imports */
import Forbidden from '../../Components/403';
import NoMatch from '../../Components/404';
import { ValidatorProps } from '../../TypeScript/interfaces';
import { useSubscriptions, useFeatureFlags } from '../helpers';
import { navigationItems } from '../navigationItems';
import Loading from '../../Components/Loading';

enum AccessStatus {
  Granted = 'granted',
  Forbidden = 'forbidden',
  NotFound = 'notfound',
  Waiting = 'waiting',
}

/**
 * Validate that the user has access to the feature based on subscription modules
 * and feature flags, using the same logic as the Sidebar navigation.
 *
 * This validator checks if a route's required modules and feature flags are
 * satisfied for the current user:
 * - Returns the child component if access is granted
 * - Returns a 403 Forbidden page if required subscription modules are missing
 * - Returns a 404 Not Found page if required feature flags are missing
 * - Returns a Loading component while subscriptions are being fetched
 *
 * @param props standard validator props with navItemId specifying which navigation item to validate
 * @returns child component if access is granted, 403 page if modules missing, 404 page if flags missing, or loading component while fetching
 *
 * @example
 * ```
 * {
 *   path: `${projectPrefix}/my-feature`,
 *   component: <MyFeaturePage />,
 *   validators: [CompanyValidator, ProjectValidator, SubscriptionAndFeatureValidator],
 *   id: 'my-feature-route',
 *   navItemId: 'my-feature-nav-key',
 * }
 * ```
 */
const SubscriptionAndFeatureValidator: React.FC<ValidatorProps> = (
  props: React.PropsWithChildren<ValidatorProps>,
): JSX.Element => {
  const { subscriptions, isLoading: isSubscriptionsLoading } =
    useSubscriptions();
  const featureFlags = useFeatureFlags();

  /**
   * Determine the access status of the current route.
   * Returns Granted if access is allowed, Forbidden if modules are missing,
   * or NotFound if feature flags are missing.
   */
  const accessStatus = useMemo((): AccessStatus => {
    // early return Waiting if subscriptions are still being fetched
    if (isSubscriptionsLoading) {
      return AccessStatus.Waiting;
    }

    const navigationKey = props.navItemId;

    // early return Granted if no navigation key is provided (no validation needed)
    if (!navigationKey) {
      return AccessStatus.Granted;
    }

    // early return Granted if there is no navigation config object for the provided key (no validation needed)
    const navItem = navigationItems[navigationKey];
    if (!navItem) {
      return AccessStatus.Granted;
    }

    const hasModules = navItem.moduleRequired.length > 0;
    const hasRequiredModules =
      hasModules &&
      navItem.moduleRequired.every((module) => subscriptions.includes(module));

    const hasFlags = (navItem.featureFlagsRequired?.length ?? 0) > 0;
    const hasRequiredFlags =
      !hasFlags ||
      (navItem.featureFlagsRequired ?? []).every((flag) =>
        featureFlags.includes(flag),
      );

    // If modules are required but not satisfied, return Forbidden
    if (hasModules && !hasRequiredModules) {
      return AccessStatus.Forbidden;
    }

    // If feature flags are required but not satisfied, return NotFound
    if (hasFlags && !hasRequiredFlags) {
      return AccessStatus.NotFound;
    }

    return AccessStatus.Granted;
  }, [props.navItemId, subscriptions, featureFlags]);

  return (
    <>
      {accessStatus === AccessStatus.Granted && props.children}
      {accessStatus === AccessStatus.Waiting && <Loading />}
      {accessStatus === AccessStatus.Forbidden && <Forbidden />}
      {accessStatus === AccessStatus.NotFound && <NoMatch />}
    </>
  );
};

export default SubscriptionAndFeatureValidator;
